import React from 'react';
import { Box, Typography } from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { Alert, AlertInline, TextInput, Button, DatePicker, Modal, ModalActions, ModalContent, SubmitButton } from '@uplift/ui-v2';
import { CalendarDateService, DateObject } from '@uplift/services';
import { useCreateProviderOutOfOfficeMutation } from '@uplift/queries';


export type OOOEvent = {
  id: string;
  title: string;
  startDate: DateObject;
  endDate: DateObject;
};

export type CreateOutOfOfficeModalProps = {
  open: boolean;
  onClose: () => void;
  providerId: string;
  existingEvents?: OOOEvent[];
};

type OOOFormValues = {
  title: string;
  startDate: DateObject | null;
  endDate: DateObject | null;
};


export const CreateOutOfOfficeModal: React.FC<CreateOutOfOfficeModalProps> = ({ open, onClose, providerId, existingEvents = [] }) => {
  const [ submitError, setSubmitError ] = React.useState<string | undefined>(undefined);
  const createOutOfOfficeMutation = useCreateProviderOutOfOfficeMutation();

  const validationSchema = React.useMemo(() => {
    const dateIsInsideEvent = (date: DateObject) =>
      existingEvents.some((e) =>
        !date.isBefore(e.startDate, 'day') && !date.isAfter(e.endDate, 'day')
      );

    const rangeWrapsEvent = (start: DateObject, end: DateObject) =>
      existingEvents.some((e) =>
        start.isBefore(e.startDate, 'day') && end.isAfter(e.endDate, 'day')
      );

    return Yup.object({
      title: Yup.string().required('Event title is required').max(40, 'Title must be 40 characters or less'),
      startDate: Yup.mixed<DateObject>()
        .required('Start date is required')
        .test('no-past-date', 'Start date cannot be in the past', function (value) {
          if (!value) return true;
          return !(value as DateObject).isBefore(CalendarDateService.date(), 'day');
        })
        .test('no-overlap', 'This date overlaps with an existing out of office event', function (value) {
          const { endDate } = this.parent as OOOFormValues;

          if (!value) return true;
          if (dateIsInsideEvent(value as DateObject)) return false;
          if (endDate && rangeWrapsEvent(value as DateObject, endDate)) return false;
          return true;
        }),
      endDate: Yup.mixed<DateObject>()
        .required('End date is required')
        .test('after-start', 'End date must be on or after the start date', function (value) {
          const { startDate } = this.parent as OOOFormValues;

          if (!startDate || !value) return true;
          return !startDate.isAfter(value as DateObject, 'day');
        })
        .test('no-overlap', 'This overlaps with an existing out of office event', function (value) {
          if (!value) return true;
          return !dateIsInsideEvent(value as DateObject);
        }),
    });
  }, [ existingEvents ]);

  return (
    <Formik<OOOFormValues>
      enableReinitialize
      validateOnMount
      initialValues={{ title: '', startDate: null, endDate: null }}
      validationSchema={validationSchema}
      onSubmit={async (values, { resetForm }) => {
        setSubmitError(undefined);
        try {
          if (values.startDate && values.endDate) {
            await createOutOfOfficeMutation.mutateAsync({
              providerId,
              eventTitle: values.title,
              startDate: values.startDate.format('YYYY-MM-DD'),
              endDate: values.endDate.format('YYYY-MM-DD'),
            });
          }
          resetForm();
          onClose();
        } catch (err) {
          console.error('[OOO submit error]', (err as { response?: { data?: unknown } })?.response?.data ?? err);
          setSubmitError('Something went wrong. Please try again.');
        }
      }}
    >
      {({ values, resetForm }) => {
        const handleClose = () => {
          resetForm();
          setSubmitError(undefined);
          onClose();
        };

        return (
          <Modal open={open} onClose={handleClose} maxWidth="md" title="Create Out of Office" showCloseIcon>
            <ModalContent>
              <Typography variant="body1" mb={2}>
                Patients will see a notification when they try to message you during this time period.
                This is something you need to coordinate coverage for.
              </Typography>
              <TextInput
                name="title"
                label="Event Title"
                placeholder="e.g., Vacation, Conference, Personal Leave"
                fullWidth
                helperText={`${values.title.length}/40`}
              />

              {/* Date row */}
              <Box display="flex" gap={2} mt={2}>
                <Box flex={1} >
                  <DatePicker
                    name="startDate"
                    label="Start Date"
                    fullWidth
                    disablePast
                    format="MM/DD/YYYY"
                    minDate={CalendarDateService.date()}
                  />
                </Box>
                <Box flex={1} >
                  <DatePicker
                    name="endDate"
                    label="End Date"
                    fullWidth
                    disablePast
                    format="MM/DD/YYYY"
                    minDate={values.startDate ?? undefined}
                  />
                </Box>
              </Box>

              <Alert severity="info" sx={{ mt: 3 }} showIcon={false}>
                <strong>Note:</strong> This does not remove or block your availability. To prevent
                bookings, you&apos;ll need to adjust your calendar separately.
              </Alert>
            </ModalContent>

            {submitError && (
              <Box sx={{ px: 3, pb: 1 }}>
                <AlertInline severity="error">{submitError}</AlertInline>
              </Box>
            )}
            <ModalActions sx={{ gap: 1 }}>
              <Button variant="outlined" onClick={handleClose}>Cancel</Button>
              <SubmitButton
                variant="contained"
                color="secondary"
                disabledOnInvalid
              >
                Create
              </SubmitButton>
            </ModalActions>
          </Modal>
        );
      }}
    </Formik>
  );
};
